import { creatorbase, creatorAgencyQueries, creatorbaseQueries } from '@round/api';
import { ModalBaseProps, Skeleton } from '@round/ui-kit';
import { Label, Modal, Button, Banner, assets } from '@round/whitelabel-ui';
import styles from './CampaignPaymentRequestModal.module.css';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { showNotification, asMoney } from 'helpers';
import PaymentRequest from 'Modules/Plans/Project/ProjectDetails/Posts/PostPaymentRequestModal/PaymentRequest/PaymentRequest';
import PaymentBreakdown from 'Modules/Plans/Project/ProjectDetails/Posts/PostPaymentRequestModal/PaymentBreakdown/PaymentBreakdown';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { mapClientPayeeToOption } from 'Modules/Plans/Finance/Payee/hooks/usePayeeSelect';
import { mapAgencyPayeeToOption } from 'Modules/Plans/Finance/Payee/hooks/useAgencyPayeeSelect';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { file } from '@round/utils';
import { FormValues, Provider, validationSchema } from 'Modules/Plans/Finance/PaymentRequest/helpers';
import PaymentRequestForm from 'Modules/Plans/Finance/PaymentRequest/PaymentRequestForm/PaymentRequestForm';
import {
    useCampaign,
    useCancelPaymentRequest,
    useClientPayees,
    useClientProviderFeeConfigs,
    useCreatePaymentRequest,
    usePostPaymentRequest,
    usePreviewPostCost,
    useProject,
    useTeam,
} from '@round/creatorbase-helpers/api-hooks';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';
import { useCurrentCampaignPaymentDetails } from './useCurrentCampaignPaymentDetails';
import { mapFormValuesToCampaignPatchData } from './helpers';
import useUpdateProjectCampaign from '../../../hooks/useUpdateProjectCampaign';
import useInvalidateProjectCampaignContext from '../../../hooks/useInvalidateProjectCampaignContext';

const { TriangleExclamation: WarningIcon } = assets;

type Props = ModalBaseProps & {
    clientId: number | undefined;
    campaignId: number;
    shouldDisablePayment?: boolean;
};

const CampaignPaymentRequestModal = ({ isOpen, onClose, clientId, campaignId, shouldDisablePayment }: Props) => {
    const queryClient = useQueryClient();
    const { getCurrencyOptionsById, getCurrencyById } = useCurrencies();
    const isCreditLimitBannerEnabled = useFeatureFlags().isEnabled('rou_211_payment_credit_limit_banner');

    const { data: campaign } = useCampaign(campaignId, { enabled: isOpen });
    const campaignCurrencyId = campaign?.currency_id;

    const { data: project } = useProject(campaign?.project_id, {
        enabled: !!campaign?.project_id && isCreditLimitBannerEnabled,
    });
    const { data: team } = useTeam(project?.team_id, {}, { enabled: !!project?.team_id && isCreditLimitBannerEnabled });

    const {
        data: latestCampaignPaymentRequest,
        isFetching: isLatestCampaignPaymentRequestFetching,
        error: latestCampaignPaymentRequestError,
        invalidate: invalidateCampaignPaymentRequests,
    } = usePostPaymentRequest(campaign?.payment_request_id, { enabled: isOpen });

    const [justSubmittedPaymentRequest, setJustSubmittedPaymentRequest] =
        useState<creatorbase.PostPaymentRequest | null>(null);
    const effectiveLatestPaymentRequest = justSubmittedPaymentRequest ?? latestCampaignPaymentRequest;

    useEffect(() => {
        if (latestCampaignPaymentRequest?.id === justSubmittedPaymentRequest?.id) {
            setJustSubmittedPaymentRequest(null);
        }
    }, [latestCampaignPaymentRequest?.id, justSubmittedPaymentRequest?.id]);

    const canSubmitPaymentRequest =
        !isLatestCampaignPaymentRequestFetching &&
        (!effectiveLatestPaymentRequest || ['FAILED', 'CANCELLED'].includes(effectiveLatestPaymentRequest.status));
    const canCancelPaymentRequest =
        !isLatestCampaignPaymentRequestFetching && effectiveLatestPaymentRequest?.status === 'PENDING';

    const invalidateProjectCampaignContext = useInvalidateProjectCampaignContext();

    const [{ mutateAsync: createPaymentRequest }, createPaymentRequestUtils] = useCreatePaymentRequest({
        onSuccess: (paymentRequest) => {
            setJustSubmittedPaymentRequest(paymentRequest);
            showNotification('Payment request submitted', 'info');
            invalidateCampaignPaymentRequests();
            invalidateCampaignPaymentDetails();
            // createPaymentRequest updates campaign.payment_request_id server-side. Invalidate
            // the campaign detail so the select in useCurrentCampaignPaymentDetails finds the
            // right payment detail (by the new ID) when the modal is reopened.
            queryClient.invalidateQueries({ queryKey: creatorbaseQueries.campaigns.keys.detail(campaignId) });
            if (campaign) {
                invalidateProjectCampaignContext({ project_id: campaign.project_id, platform: campaign.platform });
            }
            onClose();
        },
        onError: (error) => {
            const errorMessage = createPaymentRequestUtils.getErrorMessage(error) ?? 'Could not submit payment request';
            showNotification(errorMessage, 'error');
        },
    });

    const [{ mutate: cancelPaymentRequest, isPending: isCancelPaymentRequestPending }, cancelPaymentRequestUtils] =
        useCancelPaymentRequest(effectiveLatestPaymentRequest?.id);

    const { updateAsync: updateCampaign } = useUpdateProjectCampaign();

    const {
        campaignPaymentDetails,
        isFetching: areCampaignPaymentDetailsFetching,
        isLoading: isCampaignPaymentDetailsLoading,
        isSuccess: campaignPaymentDetailsIsSuccess,
        hasError: campaignPaymentDetailsError,
        file: initialFileUpload,
        hasFileError,
        invalidate: invalidateCampaignPaymentDetails,
    } = useCurrentCampaignPaymentDetails(
        { campaignId, paymentRequestId: campaign?.payment_request_id },
        { enabled: isOpen }
    );
    const hasNoExistingPaymentDetails = !campaignPaymentDetails && campaignPaymentDetailsIsSuccess;

    const handleCancelPaymentRequest = async () => {
        if (!effectiveLatestPaymentRequest?.id) {
            return;
        }

        cancelPaymentRequest(effectiveLatestPaymentRequest.id, {
            onSuccess: () => {
                setJustSubmittedPaymentRequest(null);
                showNotification('Payment request cancelled', 'info');
                invalidateCampaignPaymentRequests();
                invalidateCampaignPaymentDetails();
                if (campaign) {
                    invalidateProjectCampaignContext({
                        project_id: campaign.project_id,
                        platform: campaign.platform,
                    });
                }
            },
            onError: (error) => {
                const errorMessage =
                    cancelPaymentRequestUtils.getErrorMessage(error) ?? 'Could not cancel payment request';
                showNotification(errorMessage, 'error');
            },
        });
    };

    const handleSubmitPayment = async (helpers: FormikHelpers<FormValues>) =>
        createPaymentRequest(
            { campaign_id: campaignId },
            {
                onSuccess: () => {
                    helpers.resetForm();
                },
            }
        );

    const { data: feeConfigs } = useClientProviderFeeConfigs({ client_id: clientId });
    const getSelectedFeeConfig = (provider: Provider | null | undefined) => {
        return feeConfigs?.results.find((feeConfig) => feeConfig.provider_name === provider)?.provider_fee_config_id;
    };

    const initialPayeeId = campaignPaymentDetails?.tipalti_payee_id ?? undefined;
    const initialAgencyPayeeId = campaignPaymentDetails?.creator_agency_payee_id ?? undefined;

    const {
        data: initialPayee,
        isLoading: isInitialPayeeLoading,
        isError: isInitialPayeeError,
    } = useClientPayees(
        { tipalti_payee_id: initialPayeeId, page_size: 1, client_id: clientId },
        {
            enabled: !!initialPayeeId && !!clientId,
            select: (data) => data.results[0] || null,
        }
    );

    const { data: initialAgencyPayee, isLoading: isInitialAgencyPayeeLoading } = useQuery({
        ...creatorAgencyQueries.payee.options.list({ id: initialAgencyPayeeId, page_size: 1 }),
        select: (data) => data.results[0] || null,
        enabled: !!initialAgencyPayeeId,
    });
    const initialAgencyPayeeOption = useMemo(
        () => (initialAgencyPayee ? mapAgencyPayeeToOption(initialAgencyPayee) : null),
        [initialAgencyPayee]
    );

    const { values, setFieldValue, ...formik } = useFormik<FormValues>({
        initialValues: {
            cost: hasNoExistingPaymentDetails ? campaign?.cost : campaignPaymentDetails?.base_cost,
            currency: getCurrencyOptionsById(campaignPaymentDetails?.currency_id || campaignCurrencyId),
            notes: campaignPaymentDetails?.notes ?? '',
            fileUpload: initialFileUpload || '',
            submitType: null,
            provider: campaignPaymentDetails?.fee_config_id
                ? feeConfigs?.results.find(
                      (feeConfig) => feeConfig.provider_fee_config_id === campaignPaymentDetails?.fee_config_id
                  )?.provider_name
                : null,
            paypalEmail: campaignPaymentDetails?.paypal_email_address ?? '',
            payee: useMemo(() => (initialPayee ? mapClientPayeeToOption(initialPayee) : null), [initialPayee]),
            agencyPayee: initialAgencyPayeeOption,
        },
        onSubmit: async (values, helpers) => {
            try {
                helpers.setSubmitting(true);

                const patchData = mapFormValuesToCampaignPatchData(
                    {
                        ...values,
                        fileUpload: file.getHasFileChanged(
                            values.fileUpload || null,
                            formik.initialValues?.fileUpload || null
                        )
                            ? values.fileUpload
                            : undefined,
                    },
                    feeConfigs?.results
                );

                await updateCampaign({ id: campaignId, ...patchData });

                if (values.submitType !== 'submit') {
                    invalidateCampaignPaymentDetails();
                    showNotification('Cost updated', 'info');
                    onClose();
                    return;
                }

                await handleSubmitPayment(helpers);
            } catch {
                showNotification('Could not update cost', 'error');
            } finally {
                helpers.setSubmitting(false);
                helpers.setFieldValue('submitType', null);
            }
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
    });

    const campaignCurrency = getCurrencyOptionsById(campaignCurrencyId);
    const selectedCurrency = values.currency || campaignCurrency;

    const hasCostChanged =
        formik.dirty && isFinite(Number(values.cost)) && Number(values.cost) !== Number(formik.initialValues?.cost);
    const hasCurrencyChanged = values.currency !== formik.initialValues?.currency;
    const hasFeeConfigChanged = values.provider !== formik.initialValues?.provider;
    const hasPayeeChanged = values.payee?.value !== formik.initialValues?.payee?.value;
    const hasAgencyPayeeChanged = values.agencyPayee?.value !== formik.initialValues?.agencyPayee?.value;
    const hasFileUploadChanged = file.getHasFileChanged(
        values.fileUpload || null,
        formik.initialValues?.fileUpload || null
    );
    const hasChangedValues =
        hasCostChanged ||
        hasCurrencyChanged ||
        hasFeeConfigChanged ||
        hasPayeeChanged ||
        hasAgencyPayeeChanged ||
        hasFileUploadChanged;

    const {
        data: costPreview,
        isLoading: isPreviewLoading,
        isFetching: isPreviewFetching,
    } = usePreviewPostCost(
        {
            campaign_id: campaignId,
            base_cost: values.cost ?? undefined,
            currency_id: selectedCurrency?.value,
            fee_config_id: getSelectedFeeConfig(values.provider),
            client_payee_id: values.provider === 'TIPALTI' ? values.payee?.value : undefined,
            client_id: clientId,
            paypal_email_address: values.provider === 'PAYPAL' ? values.paypalEmail || undefined : undefined,
            creator_agency_payee_id: values.provider === 'CREATOR_AGENCY' ? values.agencyPayee?.value : undefined,
        },
        {
            enabled:
                !!values.cost &&
                isOpen &&
                (hasNoExistingPaymentDetails ||
                    campaignPaymentDetails?.payment_request_status === 'FAILED' ||
                    hasChangedValues),
        }
    );

    const defaultProvider =
        hasNoExistingPaymentDetails && !values.provider
            ? feeConfigs?.results.find((feeConfig) => costPreview?.fee_config_id === feeConfig.provider_fee_config_id)
                  ?.provider_name
            : null;

    const selectedProvider = values.provider || defaultProvider;

    const costsToUse =
        hasNoExistingPaymentDetails || campaignPaymentDetails?.payment_request_status === 'FAILED' || hasChangedValues
            ? costPreview
            : campaignPaymentDetails;

    const isBreakdownFetching = isPreviewFetching || areCampaignPaymentDetailsFetching;
    const isBreakdownLoading = isPreviewLoading || isCampaignPaymentDetailsLoading;

    const creditCurrencyCost = (() => {
        if (!costsToUse || !team || costsToUse.currency_id !== team.payment_credit_currency_id) {
            return null;
        }

        return costsToUse.cost;
    })();

    const wouldExceedCredit =
        team?.max_payment_credit != null &&
        creditCurrencyCost != null &&
        team.current_payment_credit + creditCurrencyCost > team.max_payment_credit;

    const creditCurrency = getCurrencyById(team?.payment_credit_currency_id);
    const amountOverCredit =
        wouldExceedCredit && team && creditCurrencyCost != null
            ? team.current_payment_credit + creditCurrencyCost - (team.max_payment_credit ?? 0)
            : null;

    const handleClose = () => {
        if (formik.isSubmitting) {
            return;
        }

        onClose();
        formik.resetForm();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal} closeOnOverlayClick closeOnEscape>
            <Modal.Header>
                <Modal.Title>Request Payment</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.container}>
                {isLatestCampaignPaymentRequestFetching ? (
                    <Skeleton height="4rem" />
                ) : latestCampaignPaymentRequestError ? (
                    <Banner className={styles.banner} appearance="destructive">
                        <WarningIcon className={styles.errorIcon} />
                        <p>Something went wrong fetching payment request data.</p>
                    </Banner>
                ) : effectiveLatestPaymentRequest ? (
                    <>
                        <div>
                            <Label className={styles.sectionTitle}>Payment</Label>
                            <PaymentRequest data={effectiveLatestPaymentRequest} />
                        </div>
                        {canSubmitPaymentRequest && <hr className={styles.separator} />}
                    </>
                ) : null}

                {campaignPaymentDetailsError && (
                    <Banner className={styles.banner} appearance="destructive">
                        <WarningIcon className={styles.errorIcon} />
                        <p>Something went wrong fetching payment details data.</p>
                    </Banner>
                )}
                <FormikProvider value={{ ...formik, values, setFieldValue }}>
                    <Form className={styles.formContainer}>
                        <PaymentRequestForm
                            clientId={clientId}
                            canSubmitPaymentRequest={canSubmitPaymentRequest}
                            defaultProvider={defaultProvider}
                            isLoading={
                                isCampaignPaymentDetailsLoading || isInitialPayeeLoading || isInitialAgencyPayeeLoading
                            }
                            isDisabled={isLatestCampaignPaymentRequestFetching || areCampaignPaymentDetailsFetching}
                            isInitialPayeeError={isInitialPayeeError}
                            hasFileError={!!hasFileError}
                        />

                        <div className={styles.breakdown}>
                            <PaymentBreakdown
                                isLoading={isBreakdownLoading}
                                isFetching={isBreakdownFetching}
                                costs={costsToUse}
                                selectedCurrencyId={selectedCurrency?.value}
                                campaignCurrencyId={campaignCurrencyId}
                                provider={selectedProvider}
                            />

                            {selectedProvider === 'TIPALTI' && !values.payee && (
                                <Banner className={styles.banner} appearance="warning">
                                    <WarningIcon className={styles.warningIcon} />
                                    <p>Please select a payee to see the full estimate</p>
                                </Banner>
                            )}

                            {selectedProvider === 'CREATOR_AGENCY' && !values.agencyPayee && (
                                <Banner className={styles.banner} appearance="warning">
                                    <WarningIcon className={styles.warningIcon} />
                                    <p>Please select an agency to see the full estimate</p>
                                </Banner>
                            )}

                            {isCreditLimitBannerEnabled && wouldExceedCredit && team && (
                                <Banner className={styles.banner} appearance="destructive">
                                    <WarningIcon className={styles.errorIcon} />
                                    <p>
                                        This payment would exceed the team's credit limit by{' '}
                                        <strong>{asMoney(amountOverCredit, creditCurrency)}</strong>. Current credit
                                        used: {asMoney(team.current_payment_credit, creditCurrency)} of{' '}
                                        {asMoney(team.max_payment_credit, creditCurrency)}.
                                    </p>
                                </Banner>
                            )}
                        </div>
                    </Form>
                </FormikProvider>
            </Modal.Body>
            <Modal.Actions className={styles.actions}>
                <Button
                    appearance="ghost"
                    type="button"
                    onClick={() => {
                        setFieldValue('submitType', 'save');
                        formik.submitForm();
                    }}
                    isLoading={formik.isSubmitting && values.submitType !== 'submit'}
                    disabled={
                        (!hasChangedValues && !formik.dirty && !hasNoExistingPaymentDetails) ||
                        !formik.isValid ||
                        formik.isSubmitting ||
                        isBreakdownLoading ||
                        isCancelPaymentRequestPending ||
                        !canSubmitPaymentRequest
                    }
                >
                    Save Changes
                </Button>

                {shouldDisablePayment ? null : canCancelPaymentRequest ? (
                    <Button
                        appearance="critical"
                        type="button"
                        onClick={handleCancelPaymentRequest}
                        isLoading={isCancelPaymentRequestPending}
                        disabled={isCancelPaymentRequestPending || shouldDisablePayment}
                    >
                        Cancel Payment
                    </Button>
                ) : (
                    <Button
                        appearance="primary"
                        type="button"
                        onClick={async () => {
                            setFieldValue('submitType', 'submit');
                            const errors = await formik.validateForm();
                            if (Object.keys(errors).length > 0) {
                                return;
                            }
                            formik.submitForm();
                        }}
                        isLoading={formik.isSubmitting && values.submitType === 'submit'}
                        disabled={
                            !canSubmitPaymentRequest ||
                            !formik.isValid ||
                            formik.isSubmitting ||
                            isBreakdownLoading ||
                            (isCreditLimitBannerEnabled && wouldExceedCredit)
                        }
                    >
                        {hasChangedValues || formik.dirty ? 'Save and Make Payment' : 'Make Payment'}
                    </Button>
                )}
            </Modal.Actions>
        </Modal>
    );
};

export default CampaignPaymentRequestModal;
